'use client';

import { CloseRounded } from '@mui/icons-material';
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useLocale, useTranslations } from 'next-intl';

import { signInHref } from '@/components/sign-in/sign-in-href';
import { withLocalePrefix } from '@/configurations/i18n/localePath';
import { Link, usePathname } from '@/configurations/i18n/navigation';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

import { CurrencySwitcher } from './CurrencySwitcher';
import { DestinationsMenuButton } from './DestinationsMenuButton';
import { LocaleSwitcher } from './LocaleSwitcher';
import { type NavLink, navItemFor } from './nav-links';

type Props = {
    open: boolean;
    onClose: () => void;
    /** Links rendered before the Destinations entry (role-aware). */
    preLinks: readonly NavLink[];
    /** Links rendered after the Destinations entry (role-aware). */
    postLinks: readonly NavLink[];
    /**
     * Authenticated viewer : l'avatar (UserMenu) et le panier restent dans la
     * barre, le drawer n'a donc pas besoin des actions Sign in / Sign up.
     */
    isAuthenticated: boolean;
    /**
     * Toggle devise réservé aux users authentifiés (Agency/DMC) ; masqué pour
     * les Visitors, pending inclus (#2147). Distinct de `isAuthenticated`, qui
     * reste vrai pour un visitor pending conservant son identité.
     */
    canSwitchCurrency: boolean;
};

export function MobileNavDrawer({
    open,
    onClose,
    preLinks,
    postLinks,
    isAuthenticated,
    canSwitchCurrency,
}: Props) {
    const t = useTranslations('nav');
    const locale = useLocale();
    // `usePathname` de next-intl renvoie le chemin SANS préfixe : on le rétablit pour le
    // return-to, sinon le retour après login repasse par la négociation de locale.
    const pathname = withLocalePrefix(locale, usePathname());

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: { sx: { width: 'min(85vw, 340px)', backgroundColor: 'background.default' } },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="h4" component="h2">
                    {t('menu')}
                </Typography>
                <IconButton onClick={onClose} aria-label={t('closeMenu')}>
                    <CloseRounded />
                </IconButton>
            </Box>

            <Stack spacing={1} sx={{ p: 2 }}>
                {preLinks.map(({ labelKey, href, label }) => (
                    <Typography
                        key={href}
                        component={Link}
                        href={href}
                        onClick={() => {
                            trackNavigationClick({
                                navItem: navItemFor(labelKey),
                                triggerContext: 'mobile_drawer',
                            });
                            onClose();
                        }}
                        sx={{ py: 1, color: 'text.primary', textDecoration: 'none' }}
                    >
                        {label ?? t(labelKey)}
                    </Typography>
                ))}

                <Box sx={{ py: 0.5 }}>
                    <DestinationsMenuButton isOverlay={false} triggerContext="mobile_drawer" />
                </Box>

                {postLinks.map(({ labelKey, href, label }) => (
                    <Typography
                        key={href}
                        component={Link}
                        href={href}
                        onClick={() => {
                            trackNavigationClick({
                                navItem: navItemFor(labelKey),
                                triggerContext: 'mobile_drawer',
                            });
                            onClose();
                        }}
                        sx={{ py: 1, color: 'text.primary', textDecoration: 'none' }}
                    >
                        {label ?? t(labelKey)}
                    </Typography>
                ))}
            </Stack>

            <Divider sx={{ mx: 2 }} />

            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: 'center',
                    p: 2,
                    justifyContent: 'flex-start',
                }}
            >
                {canSwitchCurrency && (
                    <CurrencySwitcher tone="solid" triggerContext="mobile_drawer" />
                )}
                <LocaleSwitcher tone="solid" triggerContext="mobile_drawer" />
            </Stack>

            {!isAuthenticated && (
                <Stack spacing={1} sx={{ px: 2, pb: 2, mt: 'auto' }}>
                    <Button
                        component={Link}
                        href={signInHref(pathname)}
                        variant="text"
                        onClick={onClose}
                        fullWidth
                    >
                        {t('signIn')}
                    </Button>
                    <Button
                        component={Link}
                        href="/sign-up"
                        variant="contained"
                        onClick={onClose}
                        fullWidth
                    >
                        {t('signUp')}
                    </Button>
                </Stack>
            )}
        </Drawer>
    );
}
